/**
 * The signed-in layout (container): feeds the session, the backend, the
 * current route and the theme into the presentational `AppShell`, and turns
 * its events into navigation, theme changes and sign-out.
 */
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { AppShell, type NavItem } from '../components/AppShell.js';
import { usePrimaryId, useServices, useSession, useTheme } from '../app/hooks.js';
import { otherTheme } from '../lib/theme.js';
import { ConnectivityControl } from './ConnectivityControl.js';

const NAV: readonly { readonly label: string; readonly href: string }[] = [
  { label: 'Rundowns', href: '/rundowns' },
  { label: 'Who am I', href: '/whoami' },
];

/** `/rundowns/<id>`: the editor, which fills the page under the top bar. */
function isEditorPath(pathname: string): boolean {
  return /^\/rundowns\/[^/]+/.test(pathname);
}

export function AppLayout() {
  const services = useServices();
  const session = useSession();
  const theme = useTheme();
  const pathname = useLocation({ select: (l) => l.pathname });
  const navigate = useNavigate();
  // Subscribed so the backend badge follows a failover (`services.backend` names the current primary) — and only that.
  usePrimaryId();

  // The guard (router.tsx) keeps signed-out users off this layout; a session
  // that ends while it is shown re-runs the guard (start.tsx).
  if (session === null) return null;

  const nav: NavItem[] = NAV.map((item) => ({ ...item, active: pathname === item.href || pathname.startsWith(`${item.href}/`) }));
  return (
    <AppShell
      backend={{ kind: services.backend.kind, label: services.backend.label }}
      user={{ userId: session.userId, email: session.email, tenantId: session.tenantId }}
      nav={nav}
      onNavigate={(href) => {
        void navigate({ to: href });
      }}
      theme={theme}
      onToggleTheme={() => services.theme.set(otherTheme(theme))}
      // Signing out is enough: start.tsx re-runs the guard whenever the session ends.
      onSignOut={() => services.sessions.signOut()}
      status={<ConnectivityControl />}
      fullBleed={isEditorPath(pathname)}
    >
      <Outlet />
    </AppShell>
  );
}
